
//! This file contains the API functions for ApplicationTests.
import { api } from "../../../services/apis/configs/axiosConfigs";

//? Importing API_URI from consts
import { API_URI } from "../../../constants/consts";

//? Importing types
import {
  type ApplicationTest,
  type GetApplicationTestsResponse,
  type CreateApplicationTestInput,
  type ApplicationTestDetails,
} from "../applicationsTests/types/types";

//? Importing types
import { type Pagination } from "../../../services/stores/types";

export const applicationTestAPI = {
  /**
   * ? Fetches all ApplicationTests for a specific application with search and pagination support.
   *
   * @param applicationId - Application ID.
   * @param search - Optional search term.
   * @param page - Page number for pagination.
   * @param token - Authentication token.
   * @param perPage - Number of items per page.
   * @returns A promise that resolves to GetApplicationTestsResponse.
   */
  getAll: async function (
    applicationId: string,
    search: string = "",
    page: number = 1,
    token: string,
    perPage: number = 10
  ): Promise<GetApplicationTestsResponse> {
    const searchQuery = search ? `&search=${search}` : "";
    const url = `${API_URI}/admin/private/applications/${applicationId}/tests?page=${page}&perPage=${perPage}${searchQuery}`;

    return api
      .request({
        url,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        const { data } = response.data;

        const pagination: Pagination = data.pagination || {
          currentPage: 0,
          totalPages: 0,
          itemsPerPage: 0,
          totalItems: 0,
        };

        return {
          applicationTests: data.data || [],
          pagination,
        };
      })
      .catch((error) => {
        console.error("Error fetching application tests:", error);
        throw new Error("Failed to fetch application tests");
      });
  },

  /**
   * TODO: Fetches a specific ApplicationTest by ID within an application.
   *
   * @param applicationId - Application ID.
   * @param id - ApplicationTest ID.
   * @param token - Authentication token.
   * @returns A promise that resolves to ApplicationTestDetails.
   */
  getById: async function (
    applicationId: string,
    id: string,
    token: string
  ): Promise<ApplicationTestDetails> {
    return api
      .request({
        url: `${API_URI}/admin/private/applications/${applicationId}/tests/${id}`,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => response.data.data)
      .catch((error) => {
        console.error("Error fetching application test:", error);
        throw new Error("Failed to fetch application test");
      });
  },

  /**
   * ! Creates a new ApplicationTest within a specific application.
   *
   * @param applicationId - Application ID.
   * @param data - Data to create the ApplicationTest.
   * @param token - Authentication token.
   * @returns A promise that resolves to ApplicationTest.
   */
  create: async function (
    applicationId: string,
    data: CreateApplicationTestInput,
    token: string
  ): Promise<ApplicationTest> {
    const payload = {
      ...data,
      application_id: applicationId,
    };

    return api
      .request({
        url: `${API_URI}/admin/private/applications/${applicationId}/tests`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: payload,
      })
      .then((response) => response.data.data)
      .catch((error) => {
        console.error("Error creating application test:", error, data);
        throw error;
      });
  },

  /**
   * ! Updates an existing ApplicationTest within a specific application.
   *
   * @param applicationId - Application ID.
   * @param id - ApplicationTest ID.
   * @param data - Data to update the ApplicationTest.
   * @param token - Authentication token.
   * @returns A promise that resolves to ApplicationTest.
   */
  update: async function (
    applicationId: string,
    id: string,
    data: CreateApplicationTestInput,
    token: string
  ): Promise<ApplicationTest> {
    const payload = {
      ...data,
      application_id: applicationId,
    };

    return api
      .request({
        url: `${API_URI}/admin/private/applications/${applicationId}/tests/${id}`,
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: payload,
      })
      .then((response) => response.data.data)
      .catch((error) => {
        console.error("Error updating application test:", error);
        throw new Error("Failed to update application test");
      });
  },

  /**
   * ? Deletes a specific ApplicationTest within an application.
   *
   * @param applicationId - Application ID.
   * @param id - ApplicationTest ID.
   * @param token - Authentication token.
   * @returns A promise that resolves to void.
   */
  delete: async function (
    applicationId: string,
    id: string,
    token: string
  ): Promise<void> {
    return api
      .request({
        url: `${API_URI}/admin/private/applications/${applicationId}/tests/${id}`,
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {})
      .catch((error) => {
        console.error("Error deleting application test:", error);
        throw new Error("Failed to delete application test");
      });
  },
};
